'use strict';
import Publisher from 'publisher';
import logger from '@main/externals/bss-package-logger/js/logger';
import helper from '@main/externals/bss-package-helper/js/helper';
import { toolbox } from '../../bss-package-toolbox/js/toolbox.js';
var _version = '@@name ver. @@version';
/**
 * Event Manager Class
 * @desc class supports event management
 */
var EventManager = /** @class */ (function () {
    function EventManager() {
        logger.info('Package: EventManager  => constructor | Params: ');
        /**
         * @desc array with subscribers objects
         * @type {Array}
         * @private
         */
        this._subscribers = [];
        /**
         * @desc owners map
         * @type {Map}
         * @private
         */
        this._owners = new Map();
        if (!this._publisher) {
            this._publisher = new Publisher();
            logger.info('Package: EventManager  => constructor | pubisher object created');
        }
    }
    /**
     * @throws error when subscribe parameters are wrong
     * @desc subscribe event and provide handler after event is published
     * @param {string} eventName - subscribed event name
     * @param {function} callback - handler called after event is published
     * @param {string} [owner=''] - event owner
     * @param {boolean} [publicEvent=false] - public event flag: true -> addEventListener to public event
     */
    EventManager.prototype.subscribe = function (eventName, callback, owner, publicEvent) {
        if (owner === void 0) { owner = ''; }
        if (publicEvent === void 0) { publicEvent = false; }
        logger.info('Package: EventManager => function :  subscribe | Params: ', eventName, callback, owner, publicEvent);
        var ownerTable;
        var eventObj;
        helper.validateMethodParams(arguments, {
            eventName: {
                isa: 'string',
            },
            callback: {
                isa: 'function',
            },
            owner: {
                isa: 'string',
                optional: true,
            },
        });
        if (publicEvent) {
            var event_1 = new CustomEvent(eventName);
            document.addEventListener(eventName, callback);
            return;
        }
        eventObj = {
            eventName: eventName,
            callback: callback,
            owner: owner,
            subscriberObject: this._publisher.subscribe(eventName, callback),
        };
        this._subscribers.push(eventObj);
        if (owner) {
            ownerTable = this._owners.get(owner);
            if (typeof ownerTable === 'undefined') {
                ownerTable = [];
            }
            ownerTable.push(eventObj);
            this._owners.set(owner, ownerTable);
        }
    };
    /**
     * @desc trigger event and send params and publisher name to subscribers
     * @param {string} eventName - published event
     * @param {string} publisherName - publisher name
     * @param {object} [params={}] - params send to subscribers
     * @param {boolean} [publicEvent=false] - event dispatch flag: true -> published event is public
     * @param {boolean} [forcedEvent=false] - event dispatch flag: true -> published event is public and not public
     */
    EventManager.prototype.publish = function (eventName, publisherName, params, publicEvent, forcedEvent) {
        if (params === void 0) { params = {}; }
        if (publicEvent === void 0) { publicEvent = false; }
        if (forcedEvent === void 0) { forcedEvent = false; }
        logger.info('Package: EventManager => function :  publish | Params: ', eventName, publisherName, params, publicEvent, forcedEvent);
        helper.validateMethodParams(arguments, {
            eventName: {
                isa: 'string',
            },
            publisherName: {
                isa: 'string',
            },
            params: {
                isa: 'object',
                optional: true,
            },
            publicEvent: {
                isa: 'boolean',
                optional: true,
            },
            forcedEvent: {
                isa: 'boolean',
                optional: true,
            },
        });
        var eventParams = {};
        if (publicEvent) {
            eventParams.params = toolbox._.cloneDeep(params);
            eventParams.publisherName = publisherName;
            var event_2 = new CustomEvent(eventName, { detail: eventParams });
            document.dispatchEvent(event_2);
            if (!forcedEvent)
                return;
        }
        eventParams = toolbox._.cloneDeep(params);
        this._publisher.publish(eventName, publisherName, eventParams);
    };
    /**
     * @desc unsubscribe event by event name and callback (and optional by owner), updates this._owners map
     * @param {string} eventName - unsubscribed event name
     * @param {function} callback - handler called after event is published
     * @param {string} [owner=''] - event owner
     */
    EventManager.prototype.unsubscribe = function (eventName, callback, owner) {
        if (owner === void 0) { owner = ''; }
        logger.info('Package: EventManager => function :  unsubscribe | Params: ', eventName, callback, owner);
        helper.validateMethodParams(arguments, {
            eventName: {
                isa: 'string',
            },
            callback: {
                isa: 'function',
            },
            owner: {
                isa: 'string',
                optional: true,
            },
        });
        var subscribers = toolbox._.filter(this._subscribers, {
            eventName: eventName,
        });
        if (owner) {
            subscribers = toolbox._.filter(subscribers, { owner: owner });
        }
        for (var i = 0, len = subscribers.length; i < len; i++) {
            subscribers[i].subscriberObject.detach();
            this._removeFromSubscribers(subscribers[i]);
            this._removeFromOwners(subscribers[i]);
        }
    };
    /**
     * @desc unsubscribe all events assigned to the owner, updates this._owners map
     * @param {string} owner - events owner
     */
    EventManager.prototype.unsubscribeByOwner = function (owner) {
        logger.info('Package: EventManager => function :  unsubscribeByOwner | Params: ', owner);
        helper.validateMethodParams(arguments, { owner: 'string' });
        var subscriberByOwners = toolbox._.filter(this._subscribers, {
            owner: owner,
        });
        for (var i = 0, len = subscriberByOwners.length; i < len; i++) {
            subscriberByOwners[i].subscriberObject.detach();
            this._removeFromSubscribers(subscriberByOwners[i]);
        }
        this._owners.delete(owner);
    };
    /**
     * removes subscriber from subscribers array
     * @param subscriber - reference to subscriber object we want to remove
     * @private
     */
    EventManager.prototype._removeFromSubscribers = function (subscriber) {
        logger.info('Package: EventManager => function :  _removeFromSubscribers | Params: ', subscriber);
        helper.validateMethodParams(arguments, {
            subscriber: {
                isa: 'object',
            },
        });
        var index = toolbox._.findIndex(this._subscribers, function (obj) {
            return (obj.eventName === subscriber.eventName &&
                obj.callback === subscriber.callback &&
                obj.owner === subscriber.owner);
        });
        toolbox._.pullAt(this._subscribers, index);
    };
    /**
     * @desc removes subscriber object from owners map / used in unsubscribe method
     * @param {Object} subscriber - reference to subscriber object we want to remove
     * @private
     */
    EventManager.prototype._removeFromOwners = function (subscriber) {
        logger.info('Package: EventManager => function :  _removeFromOwners | Params: ', subscriber);
        if (!subscriber.owner) {
            return;
        }
        helper.validateMethodParams(arguments, {
            subscriber: {
                isa: 'object',
            },
        });
        var ownerArray = this._owners.get(subscriber.owner);
        var index = toolbox._.findIndex(ownerArray, function (obj) {
            return (obj.eventName === subscriber.eventName &&
                obj.callback === subscriber.callback &&
                obj.owner === subscriber.owner);
        });
        toolbox._.pullAt(ownerArray, index);
        if (!ownerArray.length) {
            this._owners.delete(subscriber.owner);
            return;
        }
        this._owners.set(subscriber.owner, ownerArray);
    };
    Object.defineProperty(EventManager.prototype, "subscribers", {
        /**
         * @desc get subscribers array, subscribers array store objects which describes each subscription
         * @type {Object[]} array which stores all subscribers objects
         * @property {string} eventName
         * @property {function} callback
         * @property {string} owner
         * @property {Object} subscriberObject object returned by Publisher.subscribe
         */
        get: function () {
            return this._subscribers;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EventManager.prototype, "owners", {
        /**
         * @desc get owners map
         * @type {Map<string, Array<Object>>} owners map which stores subscribers objects sorted by owner
         * @property {string} key owner name
         * @property {Array<Object>} value array with objects which describes each subscription assigned to the owner
         */
        get: function () {
            return this._owners;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EventManager.prototype, "publisher", {
        /**
         * @desc get publisher object
         * @type {Object} Publisher Object
         */
        get: function () {
            return this._publisher;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @desc method return Event Manager version
     * @returns {string}
     */
    EventManager.prototype.getVersion = function () {
        return _version;
    };
    return EventManager;
}());
export default EventManager;
